import apiService from './api';

export interface Issue {
  id: string;
  title: string;
  description: string;
  status: 'new' | 'in_progress' | 'waiting' | 'done';
  priority: 'low' | 'medium' | 'high' | 'critical';
  tags: string[];
  source: 'analytics' | 'crashes' | 'debug' | 'user_feedback';
  sourceId: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  originalData: any;
  timestamp: string;
  userId?: string;
  organizationId?: string;
  deviceInfo?: any;
  appVersion?: string;
  platform?: string;
}

export interface IssueComment {
  id: string;
  text: string;
  timestamp: string;
  author: string;
  authorName: string;
  isInternal?: boolean;
}

export interface IssueFilters {
  status?: string;
  priority?: string;
  tags?: string[];
  source?: string;
  search?: string;
}

export class FirebaseIssuesService {
  async getIssues(filters?: IssueFilters): Promise<Issue[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.priority) params.append('priority', filters.priority);
      if (filters?.source) params.append('source', filters.source);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.tags && filters.tags.length > 0) {
        filters.tags.forEach(tag => params.append('tags', tag));
      }

      const response = await apiService.get(`/issues?${params.toString()}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching issues:', error);
      throw error;
    }
  }

  async updateIssue(issueId: string, updates: Partial<Issue>): Promise<void> {
    try {
      await apiService.put(`/issues/${issueId}`, updates);
    } catch (error) {
      console.error('Error updating issue:', error);
      throw error;
    }
  }

  async deleteIssue(issueId: string): Promise<void> {
    try {
      await apiService.delete(`/issues/${issueId}`);
    } catch (error) {
      console.error('Error deleting issue:', error);
      throw error;
    }
  }

  async addComment(issueId: string, comment: Omit<IssueComment, 'id' | 'timestamp'>): Promise<void> {
    try {
      await apiService.post(`/issues/${issueId}/comments`, comment);
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  }

  async getComments(issueId: string): Promise<IssueComment[]> {
    try {
      const response = await apiService.get(`/issues/${issueId}/comments`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching comments:', error);
      throw error;
    }
  }

  async getAvailableTags(): Promise<string[]> {
    try {
      const response = await apiService.get('/issues/tags');
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching tags:', error);
      return [];
    }
  }

  async getIssueStats(): Promise<{
    total: number;
    byStatus: Record<string, number>;
    byPriority: Record<string, number>;
    bySource: Record<string, number>;
  }> {
    try {
      const response = await apiService.get('/issues/stats');
      return response.data.data || {
        total: 0,
        byStatus: {},
        byPriority: {},
        bySource: {}
      };
    } catch (error) {
      console.error('Error fetching issue stats:', error);
      throw error;
    }
  }
}

export const firebaseIssuesService = new FirebaseIssuesService();